import mixpanel from 'mixpanel-browser';
import rawConfig from '../config/surveyConfig.json';

// Type declarations
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// Configuration
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const EXPERIMENT_ID = (rawConfig as any).experiment_id;

// Initialize Mixpanel
if (MIXPANEL_TOKEN) {
  console.log('Initializing Mixpanel...');
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: IS_DEVELOPMENT,
    track_pageview: true,
    persistence: 'localStorage'
  });
} else {
  console.warn('Mixpanel token not found. Analytics will be logged to console only.');
}

/**
 * Core tracking functions
 */

/**
 * Safely tracks an event in Mixpanel or logs to console if Mixpanel is not available
 */
const safeTrack = (eventName: string, properties?: Record<string, any>) => {
  if (MIXPANEL_TOKEN) {
    mixpanel.track(eventName, properties);
  } else {
    console.log(`[Analytics] ${eventName}`, properties);
  }
};

/**
 * Tracks a generic event with optional properties
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  safeTrack(eventName, properties);
};

/**
 * Identifies a user in Mixpanel and sets their properties
 */
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (MIXPANEL_TOKEN) {
    mixpanel.identify(userId);
    if (properties) {
      mixpanel.people.set({
        ...properties,
        experiment_id: EXPERIMENT_ID
      });
    } else {
      mixpanel.people.set({
        experiment_id: EXPERIMENT_ID
      });
    }
  } else {
    console.log(`[Analytics] Identify User: ${userId}`, { ...properties, experiment_id: EXPERIMENT_ID });
  }
};

/**
 * Survey Flow Events
 */

/**
 * Tracks when a user starts the survey
 */
export const trackSurveyStart = () => {
  trackEvent('Survey Started');
};

/**
 * Tracks completion of a survey step with detailed properties
 * @param stepId - Unique identifier for the step
 * @param stepTitle - Human-readable title of the step
 * @param responses - User's responses for the current step
 */
export const trackStepComplete = (stepId: string, stepTitle: string, responses: any) => {
  const eventName = stepId;
  const responseValue = responses[stepId];
  
  // Base properties for all steps
  const properties = {
    step_id: stepId,
    step_title: stepTitle,
    response_type: typeof responseValue,
    has_response: !!responseValue
  };

  // Step-specific properties
  switch (stepId) {
    case 'metal-type':
      Object.assign(properties, {
        selected_metals: responseValue,
        metal_count: responseValue?.length || 0
      });
      break;

    case 'gold-form':
    case 'silver-form':
    case 'platinum-form':
    case 'palladium-form':
      Object.assign(properties, {
        form_types: responseValue,
        form_count: responseValue?.length || 0
      });
      break;

    case 'gold-purity':
    case 'silver-purity':
    case 'platinum-purity':
    case 'palladium-purity':
      Object.assign(properties, {
        purity_levels: responseValue,
        purity_count: responseValue?.length || 0
      });
      break;

    case 'weight':
      Object.assign(properties, {
        weight_value: responseValue,
        is_unsure: responseValue === 'unsure'
      });
      break;

    case 'photos':
      Object.assign(properties, {
        photo_count: responseValue?.length || 0
      });
      break;

    case 'email':
      Object.assign(properties, {
        has_email: !!responseValue?.email
      });
      break;

    case 'phone':
      Object.assign(properties, {
        has_phone: !!responseValue?.phone,
        sms_opt_in: !responseValue?.smsOptOut
      });
      break;

    case 'name':
      Object.assign(properties, {
        has_name: !!(responseValue?.firstName && responseValue?.lastName)
      });
      break;

    case 'graphic-trust':
    case 'graphic-bestPrices':
    case 'graphic-secure':
      Object.assign(properties, {
        graphic_type: stepId.replace('graphic-', '')
      });
      break;
  }

  trackEvent(eventName, properties);
};

/**
 * Tracks when photos are uploaded
 */
export const trackPhotoUpload = (count: number) => {
  trackEvent('Photos Uploaded', { photo_count: count });
};

/**
 * Tracks metal type selection
 */
export const trackMetalSelection = (metals: string[]) => {
  trackEvent('Metals Selected', {
    metals,
    count: metals.length
  });
};

/**
 * Tracks weight entry
 */
export const trackWeightEntry = (weight: string | number) => {
  trackEvent('Weight Entered', {
    weight,
    is_unsure: weight === 'unsure'
  });
};

/**
 * Tracks form submission and related events
 */
export const trackFormSubmission = (formData: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  smsOptIn: "Yes" | "No";
  photos: string[];
  details: string;
  gclid?: string;
}) => {
  // Track form submission
  trackEvent('Form Submission', {
    has_photos: formData.photos.length > 0,
    sms_opt_in: formData.smsOptIn,
    details_length: formData.details.length,
    gclid: formData.gclid
  });
  
  // Identify user in Mixpanel
  identifyUser(formData.email, {
    name: `${formData.firstName} ${formData.lastName}`,
    sms_opt_in: formData.smsOptIn,
    has_photos: formData.photos.length > 0,
    gclid: formData.gclid
  });

  // Track Google Ads conversion
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17043062444/mzG7CP28ycIaEKz94b4_',
      'value': 1.0,
      'currency': 'USD'
    });
  }
}; 